import { LAPI_ENDPOINT, apigeeAPIKey } from '../constants'

interface CreateSSO {
  ({ destination, accountProxyId, accessToken }: SSO): Promise<SSOResponse>
}

interface SSO {
  destination: string
  accountProxyId: string
  accessToken?: string
}

interface SSOResponse {
  data: {
    createSSOForCWS: {
      isSuccessful: boolean
      samlAssertion: string
      sid: string // "pathID"
    }
  }
}

/**
 * Fetches LAPI (graphql) endpoint and if successful, returns
 * a SAML assertion to be used to SSO into Card Works
 *
 * ```tsx
 * import { createSSO } from './'
 *
 * createSSO({
 *    destination: 'example-destination',
 *    accountProxyId: 'dd010fdsasf3f93jsd23',
 *    accessToken : 'asdf23klsd90qpozl23jklsd9023'
 * }).then(response => {
 *    const samlValue = response.data.createSSOForCWS.samlAssertion
 * }).catch(() => {
 *    // log error or whatever
 * })
 * ```
 */
export const createSSO: CreateSSO = async ({
  destination,
  accountProxyId,
  accessToken,
}) => {
  try {
    const data = await fetch(LAPI_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        'API-KEY': apigeeAPIKey,
      },
      body: JSON.stringify({
        query: `          
            mutation CreateSSOForCWS {
              createSSOForCWS(input: {
                accountProxyId: "${accountProxyId}",
                destination: "${destination}",
                channel: PORTAL
              }) {
                isSuccessful
                samlAssertion
                sid
              }
            }
          `,
      }),
    }).then(res => res.json())

    /**
     * If fault object is returned, throw
     */
    /* istanbul ignore next */
    if (data.fault || data?.errors?.length > 0) {
      throw Error('LAPI fault')
    }

    return data
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(error.message)
    }
    throw new Error(String(error))
  }
}
